import { memo, useRef, useState } from "react";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import { DisaggregatedMetric } from "../../services/types";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { addCommas, getRatio } from "../../utils/helpers";
import {
    Chart,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Tooltip,
    Legend,
} from 'chart.js';

interface RaceEthnicityMetadataChartProps {
    indicators: DisaggregatedMetric[] | null;
    title: string;
    metricId: number;
    height: string;
    barColor: string;
    reverseOrder: boolean;
    sort?: string;
}

const RaceEthnicityMetadataChart = memo(({ indicators, title, metricId, height, barColor, reverseOrder, sort }: RaceEthnicityMetadataChartProps) => {
    const [data, setData] = useState<{ label: string, value: number, rawValue: number | string, originalIndex: number, isPlaceholder?: boolean }[]>([]);
    const [barColors, setBarColors] = useState<string[]>([]);
    const backgroundColor = '#125687';

    useDeepCompareEffect(() => {
        if (!indicators) return;
        console.log('indicators in race metadata:', indicators)
        const indicatorData = indicators.find(data => data.id === metricId);
        if (!indicatorData) return;

        const version = indicatorData.versions[0];
        if (!version) return;

        const tallyMap = new Map<string, number>();
        const rawValueMap = new Map<string, number | string>();

        version.disaggregations.forEach(d => {
            let percentage;
            let rawValue;

            const topLevelDenominator = version.percent_calc?.d;
            const disaggDenominator = d.percent_calc?.d;
            const disaggNumerator = d.percent_calc?.n;
        
            switch (version.unit) {
                case 'integer':
                    if (disaggDenominator === undefined || topLevelDenominator === undefined) {
                        // Case 1: No denominator for disaggregation
                        percentage = d.value / version.value; 
                        rawValue = d.value;
                    } else if (topLevelDenominator !== disaggDenominator && disaggNumerator) {
                        // Case 2: Denominators mismatch
                        percentage = disaggNumerator / disaggDenominator;
                        rawValue = disaggNumerator;
                    } else {
                        // Case 3: Denominators match
                        percentage = d.value / version.value;
                        rawValue = d.value;
                    }
                    break;
        
                case 'percentage':
                    percentage = Math.min(d.value, 1);
                    disaggNumerator ? rawValue = disaggNumerator : rawValue = d.value;
                    break;
        
                case 'decimal':
                    percentage = d.value;
                    rawValue = d.value;
                    break;
        
                case 'ratio':
                    // Calculate the reduced ratio
                    const n = disaggNumerator;
                    const dValue = d.percent_calc?.d;
        
                    if (n === undefined || dValue === undefined || dValue === 0) {
                        percentage = 0; 
                        rawValue = '0:0'; 
                    } else {
                        // Function to calculate the greatest common divisor
                        const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
                        const greatestCommonDivisor = gcd(n, dValue);
                
                        // Reduce the values to the smallest ratio
                        const reducedN = n / greatestCommonDivisor;
                        const reducedD = dValue / greatestCommonDivisor;
                
                        // Represent the percentage as a decimal value
                        percentage = reducedN / reducedD; // This is the numerical value for percentage
                        rawValue = getRatio(reducedN, reducedD, 0.05);
                    }
                    break;
        
                default:
                    percentage = 0;
                    rawValue = '0'; 
            }
        
            // Store the results in maps
            tallyMap.set(d.name.trim(), percentage);
            rawValueMap.set(d.name.trim(), rawValue);
        });

        // Convert to an array, sort by value in descending order
        let allValues = Array.from(tallyMap.entries())
            .map(([name, percentage], index) => ({
                label: name,
                value: percentage,
                rawValue: rawValueMap.get(name) ?? 0,
                originalIndex: index,
            }))

        // Sort alphabetically if the sort prop is "alphabetical"
        if (sort === "alphabetical") {
            allValues = allValues.sort((a, b) => a.label.localeCompare(b.label));
        }

        // Conditionally reverse the order of `allValues` if `reverseOrder` is true
        if (reverseOrder) {
            allValues.reverse();
        }

        // set barColors
        const barColors = allValues.map(_ => barColor);

        setData(allValues);
        setBarColors(barColors);
    }, [indicators]);


    // Register the plugin to all charts:
    Chart.register(
        CategoryScale,
        LinearScale,
        BarElement,
        BarController,
        Tooltip,
        Legend,
        ChartDataLabels
    );
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart<"bar"> | null>(null);

    useDeepCompareEffect(() => {
        const ctx = chartRef.current?.getContext("2d");
        if (ctx) {
            // Destroy existing chart instance if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            // Extract labels and data
            const labels = data.map(d => d.label);
            const chartData = data.map(d => d.value);

            // Set max and min values for label padding
            const maxValue = Math.min(Math.max(...chartData) + .05, 1);
            const minValue = Math.min(...chartData, 0);

            // Use provided barColors or fallback to backgroundColor for all bars
            const colors = barColors && barColors.length === data.length
                ? barColors
                : new Array(data.length).fill(backgroundColor);

            chartInstance.current = new Chart(ctx, {
                type: "bar",
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: title,
                            data: chartData,
                            backgroundColor: colors,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,  // Allows the chart to take the full height
                    indexAxis: 'y',
                    plugins: {
                        legend: {
                            display: false,
                        },
                        datalabels: {
                            labels: {
                                title: {
                                    anchor: 'start',
                                    align: 'start',
                                    formatter: ((value, context) => {
                                        const label = data[context.dataIndex].label;
                                        const currentIndicator = indicators?.find(data => data.id === metricId);
                                        const version = currentIndicator?.versions[0];
                                        let unit = version?.unit;

                                        const disaggregation = version?.disaggregations.find(d => d.name.trim() === label.trim());
                                        const rawValue = data[context.dataIndex]?.rawValue;

                                        if (!disaggregation) return '';

                                        const numerator = Number(disaggregation.percent_calc?.n);
                                        const denominator = Number(disaggregation.percent_calc?.d);

                                        if (unit === 'ratio') {
                                            // Use tolerance to decide if ratio should display
                                            const tolerance = 0.05;
                                            const ratioResult = getRatio(numerator, denominator, tolerance);
                                            return ratioResult !== null ? ratioResult : '';
                                        }

                                        // For other unit types
                                        if (denominator == null) {
                                            if (rawValue === 0 || rawValue === null || rawValue === undefined) {
                                                return ''; // Prevent rendering empty 0 labels
                                            } else {
                                                return `${(value * 100).toFixed(0)}%`;
                                            }
                                        }

                                        return `${(value * 100).toFixed(0)}%`;
                                    }),
                                    font: {
                                        family: 'Gotham',
                                        size: 10,
                                        weight: 'normal'
                                    },
                                    color: '#4D4D4D',
                                    offset: 0
                                },
                                // value: {
                                //     // Second label (raw value)
                                //     anchor: 'end',   // Align to the end of the bar
                                //     align: 'end',    // Align to the right end of the bar
                                //     // @ts-ignore
                                //     formatter: (value, context) => {
                                //         const rawValue = data[context.dataIndex]?.rawValue;
                                //         const name = indicators?.find(data => data.id === metricId);
                                //         const version = name?.versions[0];
                                //         const unit = version?.unit;

                                //         const originalIndex = data[context.dataIndex]?.originalIndex;
                                //         if (originalIndex === undefined) return '';

                                //         const disaggregation = version?.disaggregations[originalIndex];
                                //         const n = disaggregation?.percent_calc?.n;
                                //         const d = disaggregation?.percent_calc?.d;

                                //         // If percent_calc is missing and rawValue is 0 → hide the second label
                                //         if ((!disaggregation?.percent_calc || (n === undefined && d === undefined)) && rawValue === 0) {
                                //             return '';
                                //         }

                                //         // If n is 0, hide the label
                                //         if (n === 0) return '';

                                //         // If both n and d exist, use them
                                //         if (n !== undefined && d !== undefined) {
                                //             return `${formatValue(Number(n), 'integer')}`;
                                //         }

                                //         // If rawValue is less than or equal to 1 and unit is percentage, show percent
                                //         if (rawValue !== undefined && Number(rawValue) <= 1 && unit === 'percentage') {
                                //             return (Number(rawValue) * 100).toFixed(0).toString() + '%';
                                //         }

                                //         // Otherwise fallback to rawValue
                                //         if (rawValue !== undefined && rawValue !== null) {
                                //             return `${formatValue(Number(rawValue), 'integer')}`;
                                //         }

                                //         return '';
                                //         },
                                //     font: {
                                //         family: 'Gotham',
                                //         size: 10,
                                //         weight: 'normal',
                                //     },
                                //     color: '#4D4D4D', // Adjust color as needed
                                //     offset: 0,
                                // }
                            }
                            
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    const rawValue = data[context.dataIndex].rawValue;
                                    const name = indicators?.find(data => data.id === metricId);
                                    const versionValue = name?.versions[0]?.value;
                                    let unit = name?.versions[0]?.unit;

                                    // Retrieve the original index for this data point
                                    const originalIndex = data[context.dataIndex]?.originalIndex;
                                    if (originalIndex === undefined) return '';

                                    // Retrieve the current disaggregation item
                                    const disaggregation = name?.versions[0]?.disaggregations[originalIndex];
                                    
                                    // Check if percent_calc exists with valid n and d values
                                    const n = disaggregation?.percent_calc?.n;
                                    const d = disaggregation?.percent_calc?.d;

                                    // Special case for Ethnicity - Hispanic/Latino (show disagg numerator over total value)
                                    if (Number(name?.id) === 1072) {
                                        // Use percent_calc.n and versionValue for the tooltip
                                        return `${addCommas(Number(n))} of ${addCommas(Number(versionValue))}`;
                                    }

                                    if (n !== undefined && d !== undefined) {
                                        // Use percent_calc.n and percent_calc.d for the tooltip
                                        return `${addCommas(Number(n))} of ${addCommas(Number(d))}`;
                                    } else if (Number(rawValue) <= 1 && unit === 'percentage') {
                                        return (Number(rawValue) * 100).toFixed(0).toString() + '%';
                                    } else {
                                        // Fallback to rawValue and versionValue
                                        return `${addCommas(Number(rawValue))} of ${addCommas(Number(versionValue))}`;
                                    }
                                }
                            },
                            bodyColor: '#FFFFFF'
                        },
                    },
                    layout: {
                        padding: {
                            left: 10,
                            right: 30,
                            top: 10,
                            bottom: 10,
                        }
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            min: minValue,
                            max: maxValue,
                            grid: {
                                display: false,
                                drawTicks: true
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                display: false,
                            },
                        },
                        y: {
                            grid: {
                                display: false,
                                drawTicks: true,
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                minRotation: 0,  // Ensure labels are parallel to y-axis
                                maxRotation: 0,  // Ensure labels are parallel to y-axis
                                font: {
                                    family: 'Gotham',
                                    size: 10,
                                    weight: 'bold'
                                },
                                padding: 25,
                                color: '#4D4D4D',
                                autoSkip: false,  // Ensure all labels are displayed
                                callback: function (value: any, index: number) { 
                                // Callback to wrap long text labels
                                    const maxLabelWidth = 115;
                                    const labels = this.chart.data.labels;
                                    if (!labels) {
                                        console.log(value);
                                        return;
                                    }
                                    const label = labels[index] as string;

                                    // Wrap long labels into multiple lines by words
                                    const lines = [];
                                    let currentLine = '';
                                    const words = label.split(/\s+/);

                                    for (let word of words) {
                                        const testLine = currentLine + (currentLine ? ' ' : '') + word;
                                        // Measure the width of the test line
                                        const testWidth = this.chart.ctx.measureText(testLine).width;

                                        if (testWidth > maxLabelWidth) {
                                            if (currentLine) {
                                                lines.push(currentLine);
                                            }
                                            currentLine = word;
                                        } else {
                                            currentLine = testLine;
                                        }
                                    }
                                    lines.push(currentLine);

                                    return lines;
                                }
                            },
                        },
                    },
                },
            });
        }
    }, [data, title, backgroundColor]);

    return (
        <div className="w-full max-w-md">
            <div className={height ? height : `h-80`}> {/* Adjust height as needed */}
                <canvas ref={chartRef} className="w-full"/>
            </div>
        </div>
    );
});

export default RaceEthnicityMetadataChart;